import type { AccountType } from '@/api/accounts'
import type { TransactionSummary } from '@/api/transactions'
import { formatShortMonth } from '@/lib/formatDate'
import { fromPaise, toPaise } from '@/lib/splitMath'
import { ROUTES } from '@/routes/routes'

/**
 * What one line of the transaction list shows, worked out from the API rows. Pure, so every row
 * variant (split, refunded, capital, transfer pair, goal earmark, settlement, loan EMI, …) is
 * pinned by a unit test and the row component only renders.
 */

export type GlyphName = 'move' | 'in' | 'out' | 'goal' | 'person' | 'refund' | 'loan' | 'unknown'
export type RowIcon = { kind: 'category'; name: string } | { kind: 'glyph'; name: GlyphName }
export type TileTone = 'spend' | 'in' | 'move' | 'warn'
export type AmountTone = 'out' | 'in' | 'neutral' | 'void'
export type TagTone = 'neutral' | 'green' | 'amber'

export interface RowModel {
  key: string
  /** `transaction` and `transfer` rows take the edit/delete actions; an `emi` is managed on its loan. */
  kind: 'transaction' | 'transfer' | 'emi'
  /** The row the actions act on — a transfer's OUT leg when both legs are present. */
  tx: TransactionSummary
  /** Every transaction folded into this row, so reconciling it ticks off each leg. */
  ids: string[]
  /** Every folded transaction is ticked off against a statement. */
  reconciled: boolean
  loanAccountId?: string
  loanName?: string
  /** A same-account transfer that sets money aside for a goal. */
  earmark?: boolean
  icon: RowIcon
  tile: TileTone
  title: string
  meta: string
  metaWarn: boolean
  amount: number
  sign: '+' | '−' | ''
  tone: AmountTone
  sub?: { text: string; tone: 'green' | 'amber' | 'muted' }
  note?: string
  tags: { label: string; tone: TagTone }[]
}

export interface DayGroup {
  date: string
  /** Net spend, capital events excluded — the same rule as the hero and the API's `spent`. */
  spent: number
  income: number
  rows: RowModel[]
}

type Format = (amount: number) => string

const glyph = (name: GlyphName): RowIcon => ({ kind: 'glyph', name })

function joinMeta(...parts: (string | null | undefined)[]): string {
  return parts.filter(Boolean).join(' · ')
}

const RECURRING_TAG: Partial<Record<NonNullable<TransactionSummary['recurringKind']>, string>> = {
  SUBSCRIPTION: 'Subscription',
  BILL: 'Bill',
  OTHER: 'Recurring',
  INCOME: 'Recurring',
}

function recurringTags(tx: TransactionSummary): RowModel['tags'] {
  const label = tx.recurringKind ? RECURRING_TAG[tx.recurringKind] : undefined
  // EMI rows fold into their own "… EMI" row, which sets its own tags.
  return label && (tx.type === 'EXPENSE' || tx.type === 'INCOME')
    ? [{ label, tone: 'neutral' }]
    : []
}

function base(tx: TransactionSummary): Omit<RowModel, 'icon' | 'tile' | 'meta' | 'sign' | 'tone'> {
  return {
    key: tx.id,
    kind: 'transaction',
    tx,
    ids: [tx.id],
    reconciled: !!tx.reconciledAt,
    title: tx.description,
    metaWarn: false,
    amount: tx.amount,
    note: tx.notes || undefined,
    tags: recurringTags(tx),
  }
}

/** The account a row was paid from; a multi-account expense names its first two accounts. */
export function accountLabel(tx: TransactionSummary): string {
  const names = (tx.payments ?? []).map((p) => p.financialAccountName ?? '')
  if (names.length < 2) return tx.financialAccountName ?? ''
  return names.length === 2
    ? names.join(' + ')
    : `${names[0]} + ${names[1]} + ${names.length - 2} more`
}

/** What this account's view of a multi-account expense paid, when it is one of the parts. */
function focusPart(tx: TransactionSummary, focusAccountId?: string): number | null {
  if (!focusAccountId || (tx.payments?.length ?? 0) < 2) return null
  return tx.payments!.find((p) => p.financialAccountId === focusAccountId)?.amount ?? null
}

function expenseRow(tx: TransactionSummary, format: Format, focusAccountId?: string): RowModel {
  const paidBy = !tx.financialAccountId && tx.contactName ? tx.contactName : null
  const row: RowModel = {
    ...base(tx),
    icon: tx.categoryName ? { kind: 'category', name: tx.categoryIcon ?? '' } : glyph('unknown'),
    tile: tx.categoryName ? 'spend' : 'warn',
    meta: joinMeta(
      tx.categoryName ?? 'Uncategorised',
      paidBy ? `paid by ${paidBy}` : accountLabel(tx),
      tx.attributedMemberName
    ),
    metaWarn: !tx.categoryName,
    sign: '−',
    tone: 'out',
  }

  const sharedPaise = toPaise(tx.sharedAmount ?? 0)
  if (sharedPaise > 0) {
    const names = tx.splits?.map((s) => s.contactName) ?? []
    row.sub = {
      text: `your share ${format(fromPaise(toPaise(tx.amount) - sharedPaise))}`,
      tone: 'muted',
    }
    if (names.length > 0) {
      row.tags.push({
        label: `Split · ${names.join(', ')} ${names.length === 1 ? 'owes' : 'owe'} ${format(fromPaise(sharedPaise))}`,
        tone: 'green',
      })
    }
  }
  if (paidBy) row.sub = { text: `you owe ${paidBy}`, tone: 'amber' }

  if (tx.remainingRefundableAmount != null) {
    const refundedPaise = toPaise(tx.amount) - sharedPaise - toPaise(tx.remainingRefundableAmount)
    if (refundedPaise > 0 && toPaise(tx.remainingRefundableAmount) === 0) {
      row.tone = 'void'
      row.sign = ''
      row.sub = { text: 'fully refunded', tone: 'green' }
    } else if (refundedPaise > 0) {
      row.sub = { text: `${format(fromPaise(refundedPaise))} refunded`, tone: 'green' }
    }
  }
  if (tx.isCapitalEvent)
    row.tags.push({ label: 'Capital · not in monthly spending', tone: 'amber' })

  const part = focusPart(tx, focusAccountId)
  if (part !== null) {
    row.amount = part
    row.sub ??= { text: `of ${format(tx.amount)}`, tone: 'muted' }
  }
  return row
}

function incomeRow(tx: TransactionSummary): RowModel {
  const row: RowModel = {
    ...base(tx),
    icon: tx.categoryName ? { kind: 'category', name: tx.categoryIcon ?? '' } : glyph('in'),
    tile: 'in',
    meta: joinMeta(tx.categoryName, accountLabel(tx), tx.attributedMemberName),
    sign: '+',
    tone: 'in',
  }
  if (tx.relatedTransactionDescription) {
    row.tags.push({ label: `for ${tx.relatedTransactionDescription}`, tone: 'neutral' })
  }
  // Salary paid on 30 Sep for October reads "for Oct".
  if (tx.spendMonth && tx.spendMonth !== tx.date.slice(0, 7)) {
    row.tags.push({ label: `for ${formatShortMonth(tx.spendMonth)}`, tone: 'neutral' })
  }
  if (tx.isCapitalEvent) row.tags.push({ label: 'Capital · not in monthly income', tone: 'amber' })
  return row
}

function refundRow(tx: TransactionSummary): RowModel {
  const row: RowModel = {
    ...base(tx),
    icon: glyph('refund'),
    tile: 'in',
    meta: joinMeta(tx.categoryName, tx.financialAccountName),
    sign: '+',
    tone: 'in',
  }
  if (tx.relatedTransactionDescription) {
    row.tags.push({ label: `refund of ${tx.relatedTransactionDescription}`, tone: 'neutral' })
  }
  return row
}

function personPaymentRow(tx: TransactionSummary): RowModel {
  const incoming = tx.transferDirection === 'IN'
  const names = tx.contactName ?? tx.splits?.map((s) => s.contactName).join(', ') ?? ''
  let title = tx.description
  let label: string
  if (tx.settlementId) {
    label = 'Settle up'
    if (names) title = incoming ? `${names} paid you` : `You paid ${names}`
  } else if (names) {
    // The description says what it was for ("Concert tickets"), so it stays the title.
    label = incoming ? `Borrowed from ${names}` : `Lent to ${names}`
  } else {
    label = incoming ? 'Borrowed' : 'Lent'
  }
  return {
    ...base(tx),
    title,
    icon: glyph('person'),
    tile: incoming ? 'in' : 'spend',
    meta: joinMeta(label, tx.financialAccountName),
    sign: incoming ? '+' : '−',
    tone: incoming ? 'in' : 'out',
  }
}

function transferRow(legs: TransactionSummary[]): RowModel {
  const out = legs.find((l) => l.transferDirection === 'OUT')
  const inLeg = legs.find((l) => l.transferDirection === 'IN')
  const lead = out ?? inLeg ?? legs[0]
  const common = { ...base(lead), key: lead.transferId ?? lead.id, kind: 'transfer' as const }

  if (out && inLeg) {
    if (out.financialAccountId && out.financialAccountId === inLeg.financialAccountId) {
      return {
        ...common,
        earmark: true,
        icon: glyph('goal'),
        tile: 'in',
        meta: `Earmarked in ${out.financialAccountName ?? ''}`.trim(),
        sign: '',
        tone: 'neutral',
        tags: [{ label: 'Goal', tone: 'green' }],
      }
    }
    return {
      ...common,
      icon: glyph('move'),
      tile: 'move',
      meta: `${out.financialAccountName ?? '—'} → ${inLeg.financialAccountName ?? out.partnerAccountName ?? '—'}`,
      sign: '',
      tone: 'neutral',
    }
  }
  // Only one leg is listed when the list is filtered to one account: show that account's side.
  const incoming = lead.transferDirection === 'IN'
  return {
    ...common,
    icon: glyph(incoming ? 'in' : 'out'),
    tile: 'move',
    meta: lead.partnerAccountName
      ? `${incoming ? 'from' : 'to'} ${lead.partnerAccountName}`
      : (lead.financialAccountName ?? ''),
    sign: incoming ? '+' : '−',
    tone: incoming ? 'in' : 'out',
  }
}

function emiRow(group: TransactionSummary[], format: Format): RowModel {
  const interest = group.find((t) => t.type === 'EXPENSE')!
  const out = group.find((t) => t.type === 'TRANSFER' && t.transferDirection === 'OUT')
  const inLeg = group.find((t) => t.type === 'TRANSFER' && t.transferDirection === 'IN')
  const principal = (out ?? inLeg)!
  const loanName = out?.partnerAccountName ?? inLeg?.financialAccountName ?? 'Loan'
  return {
    ...base(interest),
    key: `emi-${interest.loanEntryId}`,
    kind: 'emi',
    loanAccountId: out?.partnerAccountId ?? inLeg?.financialAccountId,
    loanName,
    title: `${loanName} EMI`,
    icon: glyph('loan'),
    tile: 'move',
    meta: out?.financialAccountName ?? interest.financialAccountName ?? '',
    amount: fromPaise(toPaise(principal.amount) + toPaise(interest.amount)),
    sign: '',
    tone: 'neutral',
    note: undefined,
    tags: [
      { label: `principal ${format(principal.amount)}`, tone: 'neutral' },
      { label: `interest ${format(interest.amount)} · counted as spend`, tone: 'amber' },
    ],
  }
}

function singleRow(tx: TransactionSummary, format: Format, focusAccountId?: string): RowModel {
  switch (tx.type) {
    case 'INCOME':
      return incomeRow(tx)
    case 'REFUND':
      return refundRow(tx)
    case 'PERSON_PAYMENT':
      return personPaymentRow(tx)
    case 'TRANSFER':
      return transferRow([tx])
    default:
      return expenseRow(tx, format, focusAccountId)
  }
}

function withLegs(row: RowModel, legs: TransactionSummary[]): RowModel {
  return { ...row, ids: legs.map((l) => l.id), reconciled: legs.every((l) => !!l.reconciledAt) }
}

function groupKey(tx: TransactionSummary): string | null {
  if (tx.loanEntryId) return `loan:${tx.loanEntryId}`
  if (tx.type === 'TRANSFER' && tx.transferId) return `transfer:${tx.transferId}`
  return null
}

/** Rows in API order, with a transfer's legs and an EMI's principal + interest folded into one. */
export function buildRows(
  txs: TransactionSummary[],
  format: Format,
  focusAccountId?: string
): RowModel[] {
  const groups = new Map<string, TransactionSummary[]>()
  for (const tx of txs) {
    const key = groupKey(tx)
    if (key) groups.set(key, [...(groups.get(key) ?? []), tx])
  }
  const emitted = new Set<string>()
  const rows: RowModel[] = []
  for (const tx of txs) {
    const key = groupKey(tx)
    if (!key) {
      rows.push(singleRow(tx, format, focusAccountId))
      continue
    }
    if (emitted.has(key)) continue
    emitted.add(key)
    const group = groups.get(key)!
    const transfers = group.filter((t) => t.type === 'TRANSFER')
    const hasExpense = group.some((t) => t.type === 'EXPENSE')
    if (hasExpense && transfers.length > 0) rows.push(withLegs(emiRow(group, format), group))
    else if (transfers.length > 0) rows.push(withLegs(transferRow(transfers), transfers))
    else group.forEach((t) => rows.push(singleRow(t, format, focusAccountId)))
  }
  return rows
}

function daySpendPaise(tx: TransactionSummary, focusAccountId?: string): number {
  if (tx.isCapitalEvent) return 0
  if (tx.type === 'EXPENSE')
    return toPaise(focusPart(tx, focusAccountId) ?? tx.amount) - toPaise(tx.sharedAmount ?? 0)
  if (tx.type === 'REFUND') return -toPaise(tx.amount)
  return 0
}

/** One section per day, in the order the API sends them (newest first). */
export function groupRowsByDay(
  txs: TransactionSummary[],
  format: Format,
  focusAccountId?: string
): DayGroup[] {
  const byDate = new Map<string, TransactionSummary[]>()
  for (const tx of txs) byDate.set(tx.date, [...(byDate.get(tx.date) ?? []), tx])
  return Array.from(byDate, ([date, dayTxs]) => ({
    date,
    spent: fromPaise(dayTxs.reduce((sum, tx) => sum + daySpendPaise(tx, focusAccountId), 0)),
    income: fromPaise(
      dayTxs.reduce(
        (sum, tx) => (tx.type === 'INCOME' && !tx.isCapitalEvent ? sum + toPaise(tx.amount) : sum),
        0
      )
    ),
    rows: buildRows(dayTxs, format, focusAccountId),
  }))
}

/** Settlements, cash entries and refunds are undone, not edited; a refund needs an own account. */
export function actionsFor(row: RowModel) {
  const tx = row.tx
  if (row.kind === 'emi') {
    return { canEdit: false, canDelete: false, canRefund: false, canDuplicate: false }
  }
  return {
    canEdit: tx.type !== 'REFUND' && tx.type !== 'PERSON_PAYMENT',
    canDelete: true,
    canRefund: tx.type === 'EXPENSE' && !tx.contactId && (tx.remainingRefundableAmount ?? 0) > 0,
    // A copy must be something the add flow can make on its own: not a refund (tied to one
    // purchase), a settlement or loan entry (made by those features), or a goal earmark.
    canDuplicate:
      !tx.loanEntryId &&
      !tx.goalId &&
      !row.earmark &&
      (tx.type === 'EXPENSE' || tx.type === 'INCOME' || tx.type === 'TRANSFER'),
  }
}

export interface RowLink {
  label: string
  to: string
}

/** "Open …" links for whatever the row involves: people, its goal, its loan, its accounts. */
export function rowLinks(row: RowModel, accountTypes: Map<string, AccountType>): RowLink[] {
  const tx = row.tx
  const links: RowLink[] = []
  const seen = new Set<string>()
  const add = (to: string, label: string) => {
    if (seen.has(to)) return
    seen.add(to)
    links.push({ to, label })
  }
  const accountLink = (id: string | null | undefined, name: string | null | undefined) => {
    if (!id || !name) return
    add(
      accountTypes.get(id) === 'LOAN' ? ROUTES.APP.LOAN_DETAIL(id) : ROUTES.APP.ACCOUNT_DETAIL(id),
      `Open ${name}`
    )
  }

  if (tx.contactId && tx.contactName)
    add(ROUTES.APP.PERSON_DETAIL(tx.contactId), `Open ${tx.contactName}`)
  for (const s of tx.splits ?? [])
    add(ROUTES.APP.PERSON_DETAIL(s.contactId), `Open ${s.contactName}`)
  if (tx.goalId && tx.goalName) add(ROUTES.APP.GOAL_DETAIL(tx.goalId), `Open ${tx.goalName} goal`)
  if (row.kind === 'emi' && row.loanAccountId && row.loanName) {
    add(ROUTES.APP.LOAN_DETAIL(row.loanAccountId), `Open ${row.loanName}`)
  }
  accountLink(tx.financialAccountId, tx.financialAccountName)
  if (tx.type === 'TRANSFER') accountLink(tx.partnerAccountId, tx.partnerAccountName)
  return links
}
